import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Typography, Box, Tooltip, IconButton } from '@mui/material'
import { StatusChip } from '../atoms'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatNumber } from '../../services/format'
import { ResponsiveTable, TableEmptyState } from '../TableComponents'
import styles from './CampaignTable.module.scss'

const CampaignTable = ({
  campaigns = [],
  loading = false,
  showHistoricalData = false,
  showInactiveCampaigns = false,
  startDate = new Date(),
  endDate = new Date(),
  getPlanRemainingColor,
  onCampaignDetailsClick
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const renderEmptyState = () => {
    if (!loading && campaigns.length === 0 && !showHistoricalData && !showInactiveCampaigns) {
      return (
        <TableEmptyState
          title={t('campaigns.inactive')}
          message={t('metrics.noMetricsAvailable')}
          icon='filter'
        />
      )
    }

    return (
      <TableEmptyState
        title={t('campaigns.noCampaignsFound')}
        message={t('metrics.noCallsRecorded')}
        icon='database'
        dateRange={{ start: startDate, end: endDate }}
        renderCustomContent={() => (
          <>
            <Typography variant='body2' color='textSecondary' className={styles.customContentText}>
              {t('metrics.expandDateRange')}
            </Typography>
            <Typography
              variant='body2'
              color='textSecondary'
              className={styles.noteText}
            >
              Note: Campaign data is automatically synchronized daily from Five9.
            </Typography>
          </>
        )}
      />
    )
  }

  const columns = [
    {
      id: 'campaign_name',
      label: t('campaigns.name'),
      renderCell: (row) => (
        <Box display='flex' alignItems='center'>
          {row.campaign_name}
          {/* Show inactive indicator - in both views for consistency */}
          {row.call_count === 0 && (
            <StatusChip
              label={t('campaigns.inactive')}
              size='small'
              color='neutral'
              variant='soft'
            />
          )}
        </Box>
      )
    },
    {
      id: 'call_count',
      label: 'Calls',
      headerCellProps: { title: t('metrics.titles.totalCallCount') },
      align: 'right',
      renderCell: (row) => formatNumber(row.call_count)
    },
    {
      id: 'call_minutes',
      label: 'Minutes',
      headerCellProps: { title: t('metrics.titles.totalCallMinutes') },
      align: 'right',
      renderCell: (row) => formatNumber(row.call_minutes)
    },
    {
      id: 'plan_minutes',
      label: 'Plan',
      headerCellProps: { title: t('metrics.titles.planMinutes') },
      align: 'right',
      renderCell: (row) => (row.plan_minutes > 0 ? formatNumber(row.plan_minutes) : '—')
    },
    {
      id: 'plan_remaining_percent',
      label: 'Remaining',
      headerCellProps: { title: t('metrics.titles.planRemaining') },
      align: 'right',
      renderCell: (row) => (
        row.plan_minutes > 0
          ? (
            <StatusChip
              label={`${row.plan_remaining_percent}%`}
              color={getPlanRemainingColor(row.plan_remaining_percent)}
              size='small'
            />
            )
          : (
            <Typography variant='body2' color='textSecondary'>
              —
            </Typography>
            )
      )
    },
    {
      id: 'actions',
      label: t('calls.details'),
      align: 'right',
      renderCell: (row) => (
        <Box display='flex' justifyContent='flex-end'>
          <Tooltip title='View detailed statistics'>
            <IconButton
              color='primary'
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation()
                onCampaignDetailsClick(row)
              }}
            >
              <FontAwesomeIcon icon='chart-line' size='xs' />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ]

  const handleRowClick = (campaign) => {
    const encodedName = encodeURIComponent(campaign.campaign_name)
    const startISO = startDate.toISOString()
    const endISO = endDate.toISOString()
    navigate(`/campaign/${encodedName}?start=${startISO}&end=${endISO}`)
  }

  return (
    <ResponsiveTable
      columns={columns}
      data={campaigns}
      loading={loading}
      onRowClick={handleRowClick}
      variant='campaigns'
      emptyState={renderEmptyState()}
      containerProps={{
        className: styles.tableContainer
      }}
      tableProps={{
        'aria-label': 'campaign table'
      }}
    />
  )
}

CampaignTable.propTypes = {
  campaigns: PropTypes.array,
  loading: PropTypes.bool,
  showHistoricalData: PropTypes.bool,
  showInactiveCampaigns: PropTypes.bool,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  getPlanRemainingColor: PropTypes.func.isRequired,
  onCampaignDetailsClick: PropTypes.func.isRequired
}

export default CampaignTable
